'use client';
import { useEffect, useMemo, useState } from 'react';
import { useSession } from 'next-auth/react';
import { usePersistentReducer } from '../../reducers/usePersistentReducer';
import { useFretboard } from '../../hooks/useFretboard';
import { fretboardReducer, initialState } from '../../reducers/fretboardReducer';
import FretboardForm from './FretboardForm';
import {
  createCurrentBox,
  getValidBoxes,
  isValidBox,
} from '../../config/boxConfig';
import FretboardDisplaySettings from './FretboardDisplaySettings';
import LogPracticeButton from '../practice/LogPracticeButton';
import DailyPracticeLogDisplay from '../practice/DailyPracticeLogDisplay';
import ScaleInfo from './ScaleInfo';

const MODES = [
  'major',
  'minor',
  'major pentatonic',
  'minor pentatonic',
  'ionian',
  'dorian',
  'phrygian',
  'lydian',
  'mixolydian',
  'aeolian',
  'locrian',
];

const FretboardComponent = () => {
  const { data: session } = useSession();
  const [state, dispatch] = usePersistentReducer(
    fretboardReducer,
    initialState,
    'scalePracticerFretboardState'
  );
  const { renderScale, stylePresets, isInitialized } = useFretboard();

  // eslint-disable-next-line no-unused-vars
  const { root, mode, boxModeEnabled, box, currentSystem, lastFullSystem } =
    state;

  const [availableBoxes, setAvailableBoxes] = useState([]);

  // Only show the practice button when a valid scale is selected
  const canPractice = useMemo(() => {
    return root && mode;
  }, [root, mode]);

  // Render the fretboard when state changes
  useEffect(() => {
    if (!isInitialized()) return;

    const currentBox = boxModeEnabled
      ? createCurrentBox(mode, currentSystem, box)
      : undefined;

    // Handle the special case for the pentatonic box "1'"
    const renderRoot = box === '1\'' ? `${root}3` : root;

    renderScale({
      root: renderRoot,
      type: mode,
      box: currentBox,
    });
  }, [
    root,
    mode,
    box,
    boxModeEnabled,
    currentSystem,
    renderScale,
    isInitialized,
  ]);

  // Validate and fix the box when system/mode changes
  useEffect(() => {
    const validBoxes = getValidBoxes(mode, currentSystem);
    setAvailableBoxes(validBoxes);
    if (!isValidBox(mode, currentSystem, box)) {
      dispatch({ type: 'SET_BOX', payload: validBoxes[0] });
    }
  }, [dispatch, mode, currentSystem, box]);

  // Event handlers
  const handleRootChange = (newRoot) => {
    dispatch({ type: 'SET_ROOT', payload: newRoot });
  };

  const handleModeChange = (newMode) => {
    // Convert to lowercase and trim any extra whitespace
    const formattedMode = newMode.trim().toLowerCase();
    dispatch({ type: 'SET_MODE', payload: formattedMode });
  };

  return (
    <section className="">
      {/* <div className="flex justify-start"> */}
      {/* Scale Selection  */}
      <FretboardForm
        root={root}
        mode={mode}
        modes={MODES}
        onRootChange={handleRootChange}
        onModeChange={handleModeChange}
      />
      {/* </div> */}

      {/* Fretboard Display */}
      <div id="fretboard"></div>

      {/* Display Settings */}
      <div className="flex flex-row gap-4 justify-center">
        <FretboardDisplaySettings   
          dispatch={dispatch}
          boxModeEnabled={boxModeEnabled}
          mode={mode}
          currentSystem={currentSystem}
          availableBoxes={availableBoxes}
          box={box}
          stylePresets={stylePresets}
          isInitialized={isInitialized}
        />
        
        {canPractice && (
          <LogPracticeButton
            rootNote={root}
            scaleType={mode}
            isGuest={!session}
            data-testid="log-practice-button"
          />
        )}

      </div>
      
      {/* Practice Log and Scale Info */}
      <div className="flex flex-row gap-4 justify-center">
        <DailyPracticeLogDisplay session={session} dispatch={dispatch}/>
        <ScaleInfo root={root} mode={mode} box={box} boxModeEnabled={boxModeEnabled} />
      </div>

      {/* Caution: Testing Area  */}
      

    </section>
  );
};

export default FretboardComponent;
